import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { Calendar, RefreshCw, DollarSign, CreditCard, FileText, Download, FileSpreadsheet, ChevronLeft, ChevronRight, BarChart3, Eye, Printer } from 'lucide-react';
import { API_URL } from '../../config/api';
import jspdf from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const COLORS = ['#062c61', '#00BBBF', '#f59e0b', '#8b5cf6', '#06b6d4'];
const ROWS_PER_PAGE = 10;
const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const METHOD_LABELS = {
    efectivo: 'Efectivo',
    tarjeta_debito: 'Tarjeta Débito',
    tarjeta_credito: 'Tarjeta Crédito',
    transferencia: 'Transferencia',
    mixto: 'Mixto',
};
const getMethodLabel = (m) => METHOD_LABELS[m] || m || 'Sin método';

/* "?"? Helpers "?"? */
const getPresetRange = (preset) => {
    const now = new Date();
    const today = toYMD(now);
    switch (preset) {
        case 'today': return { start: today, end: today };
        case 'week': { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); return { start: toYMD(s), end: today }; }
        case 'month': return { start: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), end: today };
        case 'quarter': { const q = Math.floor(now.getMonth() / 3) * 3; return { start: toYMD(new Date(now.getFullYear(), q, 1)), end: today }; }
        case 'year': return { start: toYMD(new Date(now.getFullYear(), 0, 1)), end: today };
        default: return { start: today, end: today };
    }
};

const fmt = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

/* "?"? Custom Tooltip "?"? */
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="custom-tooltip">
            <div className="tooltip-label">{label && label.includes('-') ? new Date(label).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' }) : label}</div>
            {payload.map((e, i) => <div key={i} className="tooltip-value">{fmt(e.value)}</div>)}
        </div>
    );
};

/* "?"? Skeleton "?"? */
const SkeletonFinancial = () => (
    <>
        <div className="skeleton-grid">
            {[1, 2, 3].map(i => <div className="skeleton-card" key={i}><div className="skeleton-icon" /><div className="skeleton-text"><div className="skeleton-line short" /><div className="skeleton-line tall" /></div></div>)}
        </div>
        <div className="charts-row"><div className="skeleton-chart"><div className="skeleton-line short" /><div className="skeleton-chart-inner" /></div><div className="skeleton-chart"><div className="skeleton-line short" /><div className="skeleton-chart-inner" /></div></div>
    </>
);

const FinancialReport = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [activePreset, setActivePreset] = useState('month');
    const [dateRange, setDateRange] = useState(() => getPresetRange('month'));
    const [exportOpen, setExportOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [data, setData] = useState({ stats: { income_by_day: [], income_by_method: [], average_ticket: 0, total_income: 0 }, receipts: [] });

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/reports/financial?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!res.ok) throw new Error('Error al cargar reporte financiero');
            const result = await res.json();
            setData(result);
            setPage(1);
        } catch (err) { setError(err.message); } finally { setLoading(false); setRefreshing(false); }
    }, [dateRange]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handlePreset = (p) => { setActivePreset(p); setDateRange(getPresetRange(p)); };
    const handleDateChange = (e) => { setActivePreset(''); setDateRange(prev => ({ ...prev, [e.target.name]: e.target.value })); };

    /* "?"? Pagination "?"? */
    const totalPages = Math.max(1, Math.ceil(data.receipts.length / ROWS_PER_PAGE));
    const paginatedReceipts = useMemo(() => {
        const start = (page - 1) * ROWS_PER_PAGE;
        return data.receipts.slice(start, start + ROWS_PER_PAGE);
    }, [data.receipts, page]);

    /* "?"? Export PDF "?"? */
    const handleExportPDF = () => {
        setExportOpen(false);
        try {
            const doc = new jspdf();

            doc.setFontSize(18);
            doc.text("Reporte Financiero", 14, 20);
            doc.setFontSize(10);
            doc.text(`Periodo: ${new Date(dateRange.start).toLocaleDateString()} al ${new Date(dateRange.end).toLocaleDateString()}`, 14, 28);
            doc.text(`Generado: ${new Date().toLocaleString()}`, 14, 34);

            doc.setDrawColor(200);
            doc.setFillColor(245, 247, 250);
            doc.rect(14, 40, 180, 25, 'F');

            doc.setFontSize(10);
            doc.text("Ingreso Total", 20, 50);
            doc.setFontSize(14);
            doc.text(fmt(data.stats.total_income), 20, 58);

            doc.setFontSize(10);
            doc.text("Ticket Promedio", 80, 50);
            doc.setFontSize(14);
            doc.text(fmt(data.stats.average_ticket), 80, 58);

            doc.setFontSize(10);
            doc.text("Transacciones", 140, 50);
            doc.setFontSize(14);
            doc.text(data.receipts.length.toString(), 140, 58);

            autoTable(doc, {
                startY: 75,
                head: [["Folio", "Fecha", "Paciente", "Concepto", "Médico", "Método", "Factura", "Notas", "Total"]],
                body: data.receipts.map(r => {
                    const concepto = r.concepto || (r.items && r.items.length > 0 
                        ? r.items.map(it => `${it.descripcion || it.name}`).join(', ')
                        : '-');
                    const factura = r.solicita_factura == 1 ? (r.factura_entregada == 1 ? 'Entregada' : 'Pendiente') : 'No';
                    return [
                        r.folio, 
                        new Date(r.created_at).toLocaleDateString(), 
                        r.patient_name || '-',
                        concepto,
                        r.doctor_name || '-',
                        getMethodLabel(r.payment_method),
                        factura,
                        r.notes || '-',
                        fmt(r.total)
                    ];
                }),
                theme: 'striped',
                headStyles: { fillColor: [8, 54, 119] },
                columnStyles: { 
                    3: { fontSize: 7, cellWidth: 32 }, 
                    7: { fontSize: 7, cellWidth: 32 },
                    8: { halign: 'right' } 
                },
                styles: { fontSize: 8 }
            });

            doc.save(`Reporte_Financiero_${dateRange.start}_${dateRange.end}.pdf`);
        } catch (e) { console.error(e); setError("Error al exportar PDF"); }
    };

    /* "?"? Export Excel "?"? */
    const handleExportExcel = () => {
        setExportOpen(false);
        try {
            const wsData = [
                ["Folio", "Fecha", "Paciente", "Concepto", "Médico", "Método de Pago", "Factura", "Factura Entregada", "Notas", "Total"],
                ...data.receipts.map(r => {
                    const concepto = r.concepto || (r.items && r.items.length > 0 ? r.items.map(it => it.descripcion || it.name).join(', ') : '-');
                    const factura = r.solicita_factura == 1 ? 'Sí' : 'No';
                    const entregada = r.factura_entregada == 1 ? 'Sí' : 'No';
                    return [r.folio, new Date(r.created_at).toLocaleDateString(), r.patient_name || '-', concepto, r.doctor_name || '-', r.payment_method || '-', factura, entregada, r.notes || '-', Number(r.total) || 0];
                })
            ];
            const ws = XLSX.utils.aoa_to_sheet(wsData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Recibos");

            /* Summary sheet */
            const summaryData = [
                ["Reporte Financiero"],
                [`Periodo: ${dateRange.start} a ${dateRange.end}`],
                [],
                ["Ingreso Total", Number(data.stats.total_income) || 0],
                ["Ticket Promedio", Number(data.stats.average_ticket) || 0],
                ["Transacciones", data.receipts.length],
            ];
            const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, ws2, "Resumen");

            XLSX.writeFile(wb, `Reporte_Financiero_${dateRange.start}_${dateRange.end}.xlsx`);
        } catch (e) { console.error(e); setError("Error al exportar Excel. Asegúrate de tener la librería xlsx instalada."); }
    };

    const presets = [
        { key: 'today', label: 'Hoy' }, { key: 'week', label: 'Semana' },
        { key: 'month', label: 'Mes' }, { key: 'quarter', label: 'Trimestre' }, { key: 'year', label: 'Año' },
    ];

    return (
        <div className="reports-dashboard">
            {/* Header */}
            <div className="report-header-actions">
                <div className="report-title-wrapper">
                    <div className="report-title">
                        <BarChart3 size={28} />
                        <h2>Reporte Financiero</h2>
                    </div>
                    <p>Ingresos, métodos de pago y recibos</p>
                </div>
                <div className="report-controls">
                    <div className="date-range-picker">
                        <Calendar size={16} />
                        <input type="date" name="start" value={dateRange.start} onChange={handleDateChange} className="date-input" />
                        <span>a</span>
                        <input type="date" name="end" value={dateRange.end} onChange={handleDateChange} className="date-input" />
                    </div>
                    <button className={`btn-icon ${refreshing ? 'spinning' : ''}`} onClick={() => fetchData(true)} title="Actualizar">
                        <RefreshCw size={18} />
                    </button>
                    <div className="export-menu-container" style={{ position: 'relative' }}>
                        <button className="btn-secondary" onClick={() => setExportOpen(!exportOpen)} title="Exportar">
                            <Download size={18} />
                            <span>Exportar</span>
                        </button>
                        {exportOpen && (
                            <div className="export-dropdown" style={{ 
                                position: 'absolute', top: '100%', right: 0, 
                                background: 'white', border: '1px solid #e2e8f0', 
                                borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 1000, minWidth: '160px', marginTop: '8px',
                                animation: 'dropdown-in 0.2s ease-out', overflow: 'hidden'
                            }}>
                                <button onClick={handleExportPDF} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileText size={16} color="#ef4444" />
                                    Exportar PDF
                                </button>
                                <button onClick={handleExportExcel} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    borderTop: '1px solid #f1f5f9', fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileSpreadsheet size={16} color="#10b981" />
                                    Exportar Excel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Presets */}
            <div className="date-presets" style={{ marginBottom: '1.5rem' }}>
                {presets.map(p => (
                    <button key={p.key} className={`preset-chip ${activePreset === p.key ? 'active' : ''}`} onClick={() => handlePreset(p.key)}>
                        {p.label}
                    </button>
                ))}
            </div>

            {error && <div className="error-message">⚠️ {error}</div>}

            {loading ? <SkeletonFinancial /> : (
                <>
                    {/* KPIs */}
                    <div className="kpi-grid">
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper income"><DollarSign size={24} /></div>
                            <div><span className="kpi-title">Ingreso Total</span><span className="kpi-value">{fmt(data.stats.total_income)}</span></div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper ticket"><FileText size={24} /></div>
                            <div><span className="kpi-title">Ticket Promedio</span><span className="kpi-value">{fmt(data.stats.average_ticket)}</span></div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper count"><CreditCard size={24} /></div>
                            <div><span className="kpi-title">Transacciones</span><span className="kpi-value">{data.receipts.length}</span></div>
                        </div>
                    </div>

                    {/* Charts */}
                    <div className="charts-row">
                        <div className="chart-container">
                            <h3 className="chart-title">📈 Evolución de Ingresos</h3>
                            {data.stats.income_by_day?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={data.stats.income_by_day}>
                                        <defs>
                                            <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#00BBBF" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#00BBBF" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="day" tickFormatter={(s) => new Date(s).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area type="monotone" dataKey="total" stroke="#00BBBF" strokeWidth={2.5} fill="url(#incomeGrad)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : <div className="no-data">No hay datos en este rango</div>}
                        </div>

                        <div className="chart-container">
                            <h3 className="chart-title">💳 Métodos de Pago</h3>
                            {data.stats.income_by_method?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie data={data.stats.income_by_method} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={4} dataKey="total" nameKey="payment_method" label={({ payment_method, percent }) => `${getMethodLabel(payment_method)}: ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                                            {data.stats.income_by_method.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v) => fmt(v)} />
                                        <Legend formatter={(v) => <span style={{ color: '#334155', fontSize: '0.82rem', textTransform: 'capitalize' }}>{getMethodLabel(v)}</span>} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : <div className="no-data">No hay datos</div>}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="report-table-section">
                        <h3 className="chart-title">📊 Detalle de Recibos</h3>
                        <div className="table-responsive">
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Fecha</th>
                                        <th>Paciente</th>
                                        <th>Concepto</th>
                                        <th>Médico</th>
                                        <th>Método</th>
                                        <th>Factura</th>
                                        <th className="text-right">Total</th>
                                        <th className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedReceipts.length > 0 ? paginatedReceipts.map((r) => {
                                        const concepto = r.concepto || (r.items && r.items.length > 0 
                                            ? r.items.map(it => it.descripcion || it.name).join(', ')
                                            : '-');
                                        return (
                                        <tr key={r.id}>
                                            <td className="font-mono">{r.folio}</td>
                                            <td>{new Date(r.created_at).toLocaleDateString('es-MX')}</td>
                                            <td>{r.patient_name || '-'}</td>
                                            <td className="text-small" style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={concepto}>{concepto}</td>
                                            <td className="text-small">{r.doctor_name || '-'}</td>
                                            <td><span className={`method-badge ${r.payment_method || ''}`}>{getMethodLabel(r.payment_method)}</span></td>
                                            <td className="text-center">
                                                {r.solicita_factura == 1 ? (
                                                    r.factura_entregada == 1 
                                                        ? <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.8rem' }}>✅ Entregada</span>
                                                        : <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.8rem' }}>⏳ Pendiente</span>
                                                ) : <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>}
                                            </td>
                                            <td className="text-right font-bold">{fmt(r.total)}</td>
                                            <td className="text-center">
                                                <button 
                                                    className="action-btn view-btn" 
                                                    onClick={() => setSelectedReceipt(r)}
                                                    title="Ver detalle"
                                                    style={{ background: 'none', border: 'none', color: '#00838F', cursor: 'pointer', padding: '4px' }}
                                                >
                                                    <Eye size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                        );
                                    }) : (
                                        <tr><td colSpan="9" className="text-center">No se encontraron recibos en este periodo</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {data.receipts.length > ROWS_PER_PAGE && (
                            <div className="table-pagination">
                                <span className="pagination-info">
                                    Mostrando {(page - 1) * ROWS_PER_PAGE + 1}-{Math.min(page * ROWS_PER_PAGE, data.receipts.length)} de {data.receipts.length}
                                </span>
                                <div className="pagination-controls">
                                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={16} /></button>
                                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                        let pageNum = i + 1;
                                        if (totalPages > 5) {
                                            if (page <= 3) pageNum = i + 1;
                                            else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = page - 2 + i;
                                        }
                                        return (
                                            <button key={pageNum} className={page === pageNum ? 'active' : ''} onClick={() => setPage(pageNum)}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={16} /></button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Receipt Detail Modal (Replicated from ReceiptHistory) */}
            {selectedReceipt && (
                <div className="modal-overlay" onClick={() => setSelectedReceipt(null)} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', zIndex: 9999, padding: '20px'
                }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '500px',
                        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                    }}>
                        <div className="modal-header" style={{
                            padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a' }}>Recibo {selectedReceipt.folio}</h3>
                            <button onClick={() => setSelectedReceipt(null)} style={{ border: 'none', background: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>
                        <div className="modal-body" style={{ padding: '1.5rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem', backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px' }}>
                                <div>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', display: 'block' }}>Folio</span>
                                    <span style={{ fontWeight: 'bold' }}>{selectedReceipt.folio}</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', display: 'block' }}>Fecha</span>
                                    <span>{new Date(selectedReceipt.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', margin: '0 0 0.75rem' }}>Paciente</h4>
                                <p style={{ margin: 0, fontSize: '1rem' }}>{selectedReceipt.patient_name || '-'}</p>
                            </div>

                            {selectedReceipt.notes && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h4 style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', margin: '0 0 0.75rem' }}>Notas / Observaciones</h4>
                                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#334155', fontStyle: 'italic', backgroundColor: '#fdfae6', padding: '0.75rem', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
                                        {selectedReceipt.notes}
                                    </p>
                                </div>
                            )}

                            <div style={{ marginBottom: '1.5rem' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', margin: '0 0 0.75rem' }}>Detalle del Servicio</h4>
                                {selectedReceipt.items && selectedReceipt.items.length > 0 ? selectedReceipt.items.map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '0.95rem' }}>
                                        <span>{item.descripcion || item.name} {item.cantidad > 1 && `(x${item.cantidad})`}</span>
                                        <span style={{ fontWeight: '600' }}>{fmt(item.total)}</span>
                                    </div>
                                )) : <p style={{ margin: 0, color: '#94a3b8', fontStyle: 'italic' }}>Sin detalles disponibles</p>}
                            </div>

                            <div style={{ backgroundColor: '#f1f5f9', padding: '1rem', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                    <span>Subtotal:</span>
                                    <span>{fmt(selectedReceipt.subtotal)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#ef4444' }}>
                                    <span>Descuento:</span>
                                    <span>-{fmt(selectedReceipt.descuento)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '2px solid #e2e8f0', fontWeight: 'bold', fontSize: '1.1rem', color: '#00838F' }}>
                                    <span>TOTAL:</span>
                                    <span>{fmt(selectedReceipt.total)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                            <button onClick={() => setSelectedReceipt(null)} className="btn-secondary" style={{ padding: '0.6rem 1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancialReport;
